import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AppNotification } from '../types';
import { mockNotifications } from '../data/mockData';
import { RUNTIME } from '../config/runtime';
import { apiRequest } from '../services/api';

type ModalType =
  | 'create_dept' | 'edit_dept' | 'user_mgmt'
  | 'drive_connect' | 'drive_manage' | 'archive_cycle' | 'qr_print' | 'activity_log'
  | 'confirm_delete' | 'permission_denied' | 'doc_metadata'
  | null;

export type AppTheme = 'dark' | 'light';

interface UIState {
  // Modals
  activeModal:     ModalType;
  modalData:       Record<string, unknown>;
  openModal:       (type: ModalType, data?: Record<string, unknown>) => void;
  closeModal:      () => void;

  // Notifications
  notifications:   AppNotification[];
  notificationsUserId: string | null;
  notificationsLoading: boolean;
  hydrateNotifications: (userId: string) => Promise<void>;
  clearNotifications: () => void;
  notifPanelOpen:  boolean;
  addNotification: (n: AppNotification) => void;
  markRead:        (id: string) => Promise<void>;
  markAllRead:     () => Promise<void>;
  toggleNotifPanel:() => void;
  closeNotifPanel: () => void;

  // Scanner
  scannerOpen:     boolean;
  openScanner:     () => void;
  closeScanner:    () => void;

  // QR Print Designer
  qrDesigner:      { open: boolean; documentId: string | null };
  openQrDesigner:  (documentId: string) => void;
  closeQrDesigner: () => void;

  // Page customizer
  customizerOpen:   boolean;
  customizerDeptId: string | null;
  openCustomizer:  (deptId: string) => void;
  closeCustomizer: () => void;

  // Bottom drawer (doc metadata etc.)
  bottomSheet:     { open: boolean; type: string; data?: Record<string, unknown> };
  openSheet:       (type: string, data?: Record<string, unknown>) => void;
  closeSheet:      () => void;

  // Toast
  toast:           { visible: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' } | null;
  showToast:       (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  hideToast:       () => void;

  // Language
  lang:            'ar' | 'en';
  setLang:         (l: 'ar' | 'en') => void;

  // Appearance
  theme:           AppTheme;
  setTheme:        (theme: AppTheme) => void;
  toggleTheme:     () => void;
}

export const useUIStore = create<UIState>()(persist((set) => ({
  activeModal:  null,
  modalData:    {},
  openModal:    (type, data = {}) => set({ activeModal: type, modalData: data }),
  closeModal:   () => set({ activeModal: null, modalData: {} }),

  notifications:    RUNTIME.mode === 'cloud' ? [] : mockNotifications,
  notificationsUserId: null,
  notificationsLoading: false,
  hydrateNotifications: async (userId) => {
    set({ notificationsUserId: userId, notificationsLoading: true });
    if (RUNTIME.mode !== 'cloud') { set({ notificationsLoading: false }); return; }
    try {
      const response = await apiRequest<{ items: AppNotification[] }>('/api/notifications');
      set(state => state.notificationsUserId === userId
        ? { notifications: [...new Map(response.items.map(item => [item.id, item])).values()], notificationsLoading: false }
        : { notificationsLoading: false });
    } catch {
      set(state => state.notificationsUserId === userId ? { notifications: [], notificationsLoading: false } : { notificationsLoading: false });
    }
  },
  clearNotifications: () => set({ notifications: [], notificationsUserId: null, notificationsLoading: false, notifPanelOpen: false }),
  notifPanelOpen:   false,
  addNotification:  (n) => set(s => ({ notifications: s.notifications.some(item => item.id === n.id) ? s.notifications : [n, ...s.notifications] })),
  markRead: async (id) => {
    const previous = useUIStore.getState().notifications;
    set({ notifications: previous.map(n => n.id === id ? { ...n, read: true } : n) });
    if (RUNTIME.mode !== 'cloud') return;
    try { await apiRequest(`/api/notifications/${encodeURIComponent(id)}/read`, { method: 'PUT' }); }
    catch {
      set({ notifications: previous, toast: { visible: true, message: useUIStore.getState().lang === 'ar' ? 'تعذر حفظ حالة التنبيه.' : 'Could not save notification state.', type: 'error' } });
      throw new Error('Notification acknowledgement failed');
    }
  },
  markAllRead: async () => {
    const previous = useUIStore.getState().notifications;
    set({ notifications: previous.map(n => ({ ...n, read: true })) });
    if (RUNTIME.mode !== 'cloud') return;
    try { await apiRequest('/api/notifications/read-all', { method: 'POST' }); }
    catch {
      set({ notifications: previous, toast: { visible: true, message: useUIStore.getState().lang === 'ar' ? 'تعذر تعليم التنبيهات كمقروءة.' : 'Could not mark notifications as read.', type: 'error' } });
      throw new Error('Notification acknowledgements failed');
    }
  },
  toggleNotifPanel: () => set(s => ({ notifPanelOpen: !s.notifPanelOpen })),
  closeNotifPanel:  () => set({ notifPanelOpen: false }),

  scannerOpen:  false,
  openScanner:  () => set({ scannerOpen: true }),
  closeScanner: () => set({ scannerOpen: false }),

  qrDesigner:      { open: false, documentId: null },
  openQrDesigner:  (documentId) => set({ qrDesigner: { open: true, documentId } }),
  closeQrDesigner: () => set({ qrDesigner: { open: false, documentId: null } }),

  customizerOpen:   false,
  customizerDeptId: null,
  openCustomizer:   (deptId) => set({ customizerOpen: true, customizerDeptId: deptId }),
  closeCustomizer:  () => set({ customizerOpen: false, customizerDeptId: null }),

  bottomSheet: { open: false, type: '' },
  openSheet:   (type, data) => set({ bottomSheet: { open: true, type, data } }),
  closeSheet:  () => set({ bottomSheet: { open: false, type: '' } }),

  toast:     null,
  showToast: (message, type = 'info') => {
    set({ toast: { visible: true, message, type } });
    setTimeout(() => set({ toast: null }), 3500);
  },
  hideToast: () => set({ toast: null }),

  lang:    'ar',
  setLang: (l) => set({ lang: l }),

  theme:       'dark',
  setTheme:    (theme) => set({ theme }),
  toggleTheme: () => set((state) => ({ theme: state.theme === 'dark' ? 'light' : 'dark' })),
}), {
  name: 'masar-pharmacy-ui',
  partialize: (state) => ({ lang: state.lang, theme: state.theme }),
}));
